import { useMemo } from "react";
import { Flex, Avatar, Division } from "@ir/ui";
import { IMessage } from "@/core/entities/conversation";
import { ConversationUtils } from "./utils/ConversationUtils";
import MessageItemFiles from "./InternalConversationFiles";
import { useWorkspace } from "@/hooks";
import { useConversations } from "@/presentation/pages/conversations/hooks/useConversations";

const escapeRegexKeyword = (keyword: string): string =>
  keyword.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

const splitByHtmlTags = (html: string): string[] => {
  const segments: string[] = [];
  let cursor = 0;

  while (cursor < html.length) {
    const openIndex = html.indexOf("<", cursor);

    if (openIndex === -1) {
      segments.push(html.slice(cursor));
      break;
    }

    if (openIndex > cursor) {
      segments.push(html.slice(cursor, openIndex));
    }

    const closeIndex = html.indexOf(">", openIndex + 1);

    if (closeIndex === -1) {
      segments.push(html.slice(openIndex));
      break;
    }

    segments.push(html.slice(openIndex, closeIndex + 1));
    cursor = closeIndex + 1;
  }

  return segments.length > 0 ? segments : [html];
};

const highlightHtmlContent = (html: string, keywords: string[]): string => {
  if (!html || keywords.length === 0) return html;

  const escapedKeywords = keywords
    .filter((keyword) => keyword.trim().length > 0)
    .map(escapeRegexKeyword)
    .sort((a, b) => b.length - a.length);

  if (escapedKeywords.length === 0) return html;

  const regexes = escapedKeywords.map((keyword) => new RegExp(`(${keyword})`, "gi"));

  return splitByHtmlTags(html)
    .map((segment) => {
      if (/^<[^>]+>$/.test(segment)) {
        return segment;
      }

      return regexes.reduce(
        (updatedSegment, regex) => updatedSegment.replace(regex, '<mark class="highlighter">$1</mark>'),
        segment
      );
    })
    .join("");
};

const MessageItem = ({ message }: { message: IMessage }) => {
  const msgClass = message.isOwner ? "msg right-msg" : "msg left-msg";
  const { settings } = useWorkspace();
  const { searchQuery, currentConversation } = useConversations();
  const highlightedMessageHtml = useMemo(() => {
    if (currentConversation) {      
      return highlightHtmlContent(message.contentHtml ?? "", searchQuery ? [searchQuery] : []);
    }

    return message.contentHtml ?? "";
  }, [message.contentHtml, searchQuery, currentConversation]);

  const isPending = message.id === null;

  return (
    <Flex className={msgClass}>
      {!message.isOwner && (
        <Flex className="msg-avatar">
          <Avatar size="small" name={message?.sender?.fullName ?? ""} />
        </Flex>
      )}

      <Division className="msg-bubble">
        <Flex className="msg-info">
          <Flex className="msg-info-name">{message?.sender?.fullName ?? ""}</Flex>
          <Flex className="msg-info-time">
            {ConversationUtils.formatLocalDateTime(message.createdAt!, settings?.displayTimeFormat)}
          </Flex>
        </Flex>
        {message.contentHtml && (
          <Division
            className={`msg-text${message.isError ? " error" : ""}`}
            dangerouslySetInnerHTML={{ __html: highlightedMessageHtml }}
          />
        )}
        <MessageItemFiles files={message.attachments} isPending={isPending}/>
      </Division>
    </Flex>
  );
};

export default MessageItem;
